import { useState } from "react";
import { Alert, Box, Button, Switch, Typography } from "@mui/material";
import { useNotify, useRecordContext, useRefresh } from "react-admin";
import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { ResourceName, type Policy } from "@/types";
import { ConfirmLabelType } from "@/types/shared/uiComponents";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useActivePartner } from "@/hooks/useActivePartner";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";

/**
 * Rx carve-out state + manual override (ADR-077 §2 / #5851). Shows the effective
 * carve-out flag and whether it's inferred from the vendor sets or manually
 * overridden, lets an editor set the override for the exception case, and raises
 * a "carve-out undetermined" alarm when inference is ambiguous (the RX vendor set
 * overlaps the TPA set with no override set).
 *
 * On/off only — the override is an explicit true/false; there is intentionally no
 * clear-to-inferred control (no tri-state, per the agreed decision). Two UX
 * guards, both from the !4208 review:
 *  - In the review-needed (overlap) state the seeded-OFF Switch could only reach
 *    `true` in one click, making the likely-correct "bundled" resolution
 *    unreachable — so that state presents both resolutions explicitly
 *    ("Mark carved out" / "Mark bundled"), each one deliberate action.
 *  - Every override change confirms first: flipping engages/disengages per-type
 *    authority for the whole policy (blast radius to stop-loss / breach / tiles),
 *    and detaches an inferred policy from vendor inference — a consequential,
 *    hard-to-reverse action, so it isn't a single immediate click.
 *
 * Edit affordance is gated on policies edit permission AND a writable partner;
 * the server-side guard remains the enforcement of record.
 */
export const RxCarveOutSection = () => {
  const policy = useRecordContext<Policy>();
  const { canEdit } = useResourcePermissions(ResourceName.POLICIES);
  const { readOnly } = useActivePartner();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  // The override value awaiting confirmation (null = no dialog open).
  const [pending, setPending] = useState<boolean | null>(null);

  if (!policy) return null;

  const effective = !!policy.rx_carve_out_effective;
  const inferred = !!policy.rx_carve_out_inferred;
  const reviewNeeded = !!policy.rx_carve_out_review_needed;
  const isOverridden = policy.rx_carve_out_override !== null && policy.rx_carve_out_override !== undefined;
  const editable = canEdit && !readOnly;

  const setOverride = async (value: boolean) => {
    setBusy(true);
    try {
      await httpClient(`${getPartnerAwareApiUrl()}/policies/${policy.id}`, {
        method: "PATCH",
        body: JSON.stringify({ rx_carve_out_override: value }),
      });
      notify("Rx carve-out updated", { type: "success" });
      refresh();
    } catch (error) {
      notifyError(error);
    } finally {
      setBusy(false);
    }
  };

  const confirmMessage =
    (pending
      ? "Marking this policy as an Rx carve-out engages per-claim-type authority — medical and Rx claim totals resolve separately. "
      : "Marking this policy as bundled uses a single combined authority for the whole claimant. ") +
    "This changes how claim totals are calculated and cascades to stop-loss, breach, and claimant totals." +
    (isOverridden ? "" : " It also sets a manual override, so the flag will stop tracking vendor-set changes.");

  return (
    <ShowAccordionSection label="Rx Carve-Out" defaultExpanded>
      {reviewNeeded && (
        <Alert severity="warning" sx={{ mb: 1.5, "& .MuiAlert-message": { fontSize: "0.8125rem" } }}>
          Carve-out undetermined — the Rx vendor set overlaps the TPA set, so it can&rsquo;t be inferred. Mark it carved
          out or bundled to resolve it.
        </Alert>
      )}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2">
            Rx carved out: <strong>{effective ? "Yes" : "No"}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isOverridden ? "Manually overridden" : `Inferred from vendors (${inferred ? "carve-out" : "bundled"})`}
          </Typography>
        </Box>
        {reviewNeeded ? (
          // Overlap: both resolutions reachable in one deliberate action, rather
          // than a Switch seeded OFF that can only reach `true` (#22908).
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button size="small" variant="outlined" disabled={!editable || busy} onClick={() => setPending(true)}>
              Mark carved out
            </Button>
            <Button size="small" variant="outlined" disabled={!editable || busy} onClick={() => setPending(false)}>
              Mark bundled
            </Button>
          </Box>
        ) : (
          <Switch
            checked={effective}
            disabled={!editable || busy}
            onChange={(e) => setPending(e.target.checked)}
            inputProps={{ "aria-label": "Rx carve-out override" }}
          />
        )}
      </Box>
      <ConfirmationDialog
        open={pending !== null}
        title={pending ? "Mark policy as Rx carve-out?" : "Mark policy as bundled?"}
        message={confirmMessage}
        confirmLabel={ConfirmLabelType.Continue}
        onConfirm={() => {
          const value = pending;
          setPending(null);
          if (value !== null) void setOverride(value);
        }}
        onCancel={() => setPending(null)}
      />
    </ShowAccordionSection>
  );
};
